import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import { LogoMainIcon } from '@/components/brand';
import { TickCircle, InfoCircle, Spinner, Lock1, Send2, Tag } from '@/components/icons';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatSupportDate, SUPPORT_CATEGORY_LABELS } from '../constants';
import { SupportStatusBadge } from './SupportStatusBadge';
import type { SupportTicketDetail, SupportTicketMessage } from '@/api/support';

type SupportThreadProps = {
    ticket: SupportTicketDetail;
    perspective: 'user' | 'agent';
    isSending: boolean;
    onSendMessage: (body: string) => Promise<unknown> | unknown;
    onCloseTicket?: () => Promise<unknown> | unknown;
    isClosing?: boolean;
    onRedactMessage?: (messageId: string) => Promise<unknown> | unknown;
    isRedactingMessageId?: string | null;
    showHeader?: boolean;
    headerLeading?: ReactNode;
};

const getMessageAuthorLabel = (message: SupportTicketMessage, perspective: 'user' | 'agent') => {
    if (message.author_type === 'support') return 'Suporte LaudoPsi';
    return perspective === 'agent' ? 'Psicólogo' : 'Você';
};

const isSameDay = (left: string, right: string) => {
    const leftDate = new Date(left);
    const rightDate = new Date(right);

    return leftDate.toDateString() === rightDate.toDateString();
};

const getDateSeparatorLabel = (value: string) => {
    const date = new Date(value);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Hoje';
    if (date.toDateString() === yesterday.toDateString()) return 'Ontem';

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
};

const getShortTime = (value: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));
};

export function SupportThread({
    ticket,
    perspective,
    isSending,
    onSendMessage,
    onCloseTicket,
    isClosing = false,
    onRedactMessage,
    isRedactingMessageId = null,
    showHeader = true,
    headerLeading,
}: SupportThreadProps) {
    const [message, setMessage] = useState('');
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const messagesWithMetadata = useMemo(() => {
        return ticket.messages.map((item, index) => {
            const previous = ticket.messages[index - 1];
            const next = ticket.messages[index + 1];
            const startsRun = previous?.author_type !== item.author_type;
            const endsRun = next?.author_type !== item.author_type;
            const startsDate = !previous || !isSameDay(previous.created_at, item.created_at);

            return {
                item,
                startsRun,
                endsRun,
                startsDate,
            };
        });
    }, [ticket.messages]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
    }, [ticket.id, ticket.messages.length]);

    // Starts at one line and grows with the draft, capped by `max-h-32`. A fixed
    // two-row box costs the message list ~50px on every phone, whether or not
    // anyone is typing.
    useEffect(() => {
        const node = textareaRef.current;
        if (!node) return;

        node.style.height = 'auto';
        node.style.height = `${node.scrollHeight}px`;
    }, [message]);

    const handleSubmit = async () => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage || isSending) return;

        try {
            await onSendMessage(trimmedMessage);
            setMessage('');
        } catch {
            // Parent mutations surface errors; keep the draft available for retry.
        }
    };

    const handleMessageKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== 'Enter' || (!event.metaKey && !event.ctrlKey)) return;

        event.preventDefault();
        void handleSubmit();
    };

    const handleCloseTicket = async () => {
        if (!onCloseTicket || isClosing) return;

        try {
            await onCloseTicket();
        } catch {
            // Parent mutations surface errors.
        }
    };

    const handleRedactMessage = async (messageId: string) => {
        if (!onRedactMessage || isRedactingMessageId) return;

        try {
            await onRedactMessage(messageId);
        } catch {
            // Parent mutations surface errors.
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden border-border/80 bg-card/95 md:rounded-2xl md:border md:shadow-elevation-1">
            {showHeader ? (
                <div className="shrink-0 border-b border-border/70 bg-surface-muted/50 p-3 md:p-5">
                    {/* Always a row. Stacking this header is what left a phone with
                        153px of conversation: every line here is chrome the
                        message list pays for. */}
                    <div className="flex items-start gap-3">
                        {headerLeading ? <div className="shrink-0">{headerLeading}</div> : null}
                        <div className="min-w-0 flex-1 space-y-1.5 md:space-y-2">
                            <h2 className="truncate text-base font-semibold tracking-tight text-foreground md:text-xl">
                                {ticket.subject}
                            </h2>
                            {/* Timestamps and category are reference information —
                                they lose to the conversation on a small screen. */}
                            <p className="hidden text-sm text-muted-foreground md:block">
                                Aberto em {formatSupportDate(ticket.created_at)} · atualizado em{' '}
                                {formatSupportDate(ticket.updated_at)}
                            </p>
                            {perspective === 'agent' && ticket.requester ? (
                                <p className="truncate text-xs text-muted-foreground md:text-sm">
                                    {ticket.requester.full_name} · {ticket.requester.email}
                                </p>
                            ) : null}
                            <div className="flex flex-wrap items-center gap-2">
                                <SupportStatusBadge
                                    status={ticket.status}
                                    perspective={perspective}
                                    showDot
                                    className="rounded-full border-transparent bg-brand text-primary-foreground"
                                />
                                <Badge
                                    variant="outline"
                                    className="hidden gap-1.5 rounded-full bg-card text-muted-foreground md:inline-flex"
                                >
                                    <Tag className="size-3" />
                                    {SUPPORT_CATEGORY_LABELS[ticket.category]}
                                </Badge>
                            </div>
                        </div>
                        {onCloseTicket && ticket.status !== 'closed' ? (
                            <>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="size-10 shrink-0 lg:hidden"
                                    onClick={handleCloseTicket}
                                    disabled={isClosing}
                                    aria-label="Marcar como resolvido"
                                    title="Marcar como resolvido"
                                >
                                    {isClosing ? (
                                        <Spinner className="size-4 animate-spin" />
                                    ) : (
                                        <TickCircle className="size-4" />
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="hidden shrink-0 lg:inline-flex"
                                    onClick={handleCloseTicket}
                                    disabled={isClosing}
                                >
                                    {isClosing ? (
                                        <Spinner className="size-4 animate-spin" />
                                    ) : (
                                        <TickCircle className="size-4" />
                                    )}
                                    Marcar como resolvido
                                </Button>
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <div
                role="log"
                aria-label="Mensagens do chamado"
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-surface/60 p-3 md:p-5"
            >
                <div className="w-full">
                    {messagesWithMetadata.map(({ item, startsRun, endsRun, startsDate }) => {
                        const isSupport = item.author_type === 'support';
                        const isOwn = perspective === 'agent' ? isSupport : !isSupport;
                        const showAuthorLabel = startsRun && !(perspective === 'user' && isOwn);

                        return (
                            <div key={item.id}>
                                {startsDate ? (
                                    <div className="flex justify-center py-4">
                                        <span className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm">
                                            {getDateSeparatorLabel(item.created_at)}
                                        </span>
                                    </div>
                                ) : null}
                                <div
                                    className={cn(
                                        'flex',
                                        startsRun ? 'mt-5' : 'mt-1',
                                        isOwn ? 'justify-end' : 'justify-start',
                                    )}
                                >
                                    {!isOwn ? (
                                        <div className="mr-2 flex w-8 shrink-0 justify-center">
                                            {startsRun ? (
                                                <div
                                                    className="flex size-8 items-center justify-center rounded-full border border-brand/15 bg-card text-xs font-bold text-brand"
                                                    title={getMessageAuthorLabel(item, perspective)}
                                                >
                                                    {isSupport ? <LogoMainIcon /> : 'PS'}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                    <div
                                        className={cn(
                                            'min-w-0 max-w-[85%] space-y-2 border px-4 py-3 shadow-sm sm:min-w-[140px] md:max-w-[90%]',
                                            isOwn
                                                ? 'rounded-2xl rounded-br bg-brand text-primary-foreground'
                                                : 'rounded-2xl rounded-bl border-border bg-card text-card-foreground',
                                        )}
                                    >
                                        {showAuthorLabel ? (
                                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span
                                                    className={cn(
                                                        'font-semibold',
                                                        isOwn
                                                            ? 'text-primary-foreground'
                                                            : 'text-foreground',
                                                    )}
                                                >
                                                    {getMessageAuthorLabel(item, perspective)}
                                                </span>
                                            </div>
                                        ) : null}
                                        {item.redacted_at ? (
                                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning">
                                                    <Lock1 className="size-3" />
                                                    Redigida
                                                </span>
                                            </div>
                                        ) : null}
                                        <p className="whitespace-pre-wrap break-words text-left text-sm leading-relaxed">
                                            {item.body}
                                            {endsRun ? (
                                                <span
                                                    className={cn(
                                                        'float-right ml-3 mt-1 text-[11px] leading-4',
                                                        isOwn
                                                            ? 'text-primary-foreground/85'
                                                            : 'text-muted-foreground',
                                                    )}
                                                >
                                                    {getShortTime(item.created_at)}
                                                </span>
                                            ) : null}
                                        </p>
                                        {perspective === 'agent' &&
                                        !item.redacted_at &&
                                        onRedactMessage ? (
                                            <div className="pt-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        'h-7 px-2 text-xs',
                                                        isOwn
                                                            ? 'text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground'
                                                            : 'text-muted-foreground',
                                                    )}
                                                    onClick={() => handleRedactMessage(item.id)}
                                                    disabled={isRedactingMessageId === item.id}
                                                >
                                                    {isRedactingMessageId === item.id ? (
                                                        <Spinner className="size-3 animate-spin" />
                                                    ) : null}
                                                    Redigir mensagem
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Not sticky: this is the last child of a fixed-height flex column,
                so it is already pinned. Taking it out of flow would let the
                message list run underneath it again. */}
            <div className="shrink-0 border-t border-border/70 bg-card p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:p-5">
                <div className="space-y-2 md:space-y-3">
                    <div className="flex items-end gap-2 md:gap-3">
                        <Textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                                setMessage(event.target.value)
                            }
                            onKeyDown={handleMessageKeyDown}
                            maxLength={4000}
                            rows={1}
                            className="max-h-32 min-h-11 resize-none"
                            placeholder={
                                perspective === 'agent'
                                    ? 'Responder ao psicólogo...'
                                    : ticket.status === 'closed'
                                      ? 'Responder reabrirá este chamado automaticamente...'
                                      : 'Adicionar uma resposta ao suporte...'
                            }
                        />
                        <Button
                            size="icon"
                            onClick={handleSubmit}
                            className={cn(
                                'inline-flex size-11 shrink-0 items-center justify-center md:size-10',
                                message.trim() &&
                                    'bg-brand text-primary-foreground hover:bg-brand/90',
                            )}
                            disabled={!message.trim() || isSending}
                            aria-label="Enviar resposta"
                            title="Enviar resposta"
                        >
                            {isSending ? (
                                <Spinner className="size-4 animate-spin" />
                            ) : (
                                <Send2 className="size-4" />
                            )}
                        </Button>
                    </div>
                    {perspective === 'user' ? (
                        <div className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
                            <InfoCircle className="mt-0.5 size-3.5 shrink-0 text-warning" />
                            <span className="md:hidden">
                                Não envie dados de pacientes ou informações clínicas.
                            </span>
                            <span className="hidden md:inline">
                                Não envie dados de pacientes, laudos, instrumentos, documentos ou
                                informações clínicas identificáveis.
                            </span>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
